<template>
  <div v-show="isCustomFieldValid">
    <label
      v-if="!props.hideLabel"
      class="input-label tw-flex tw-items-center"
      :for="`${customField?.name}`"
    >
      {{ customField?.name }}
      <span
        v-if="props.customField.required"
        class="tw-ml-0.5 tw-text-sm tw-text-red"
      >
        *
      </span>
      <help-text v-if="!!customField.description" class="tw-mb-0.5 tw-ml-1">
        {{ customField.description }}
      </help-text>
    </label>
    <q-input
      :id="`${customField?.name}`"
      v-model.number="localVal"
      :for="`${customField?.name}`"
      :name="`${customField?.name}`"
      :readonly="props.readonly"
      dense
      outlined
      class="input-xs tw-w-full tw-bg-white"
      :class="{
        'form-input--error': !!errorMessage && showErrorMessage,
      }"
      :placeholder="placeholderText"
      :step="step"
      :min="min"
      :max="max"
      type="number"
      @blur="onBlur"
      @keydown.enter="onEnter"
      @keydown="checkForWholeNumber"
      @keydown.escape.stop="onEscape"
    >
      <template v-if="isPercentageField" #append>
        <span>%</span>
      </template>
    </q-input>
    <span
      v-if="!!errorMessage && showErrorMessage"
      class="form-input__error-message"
    >
      {{ errorMessage }}
    </span>
  </div>
</template>

<script setup lang="ts">
import { isNil } from 'lodash-es';
import { ref, computed, nextTick, watch } from 'vue';
import HelpText from '../design-system/misc/HelpText.vue';
import {
  CustomField,
  CustomFieldType,
  CustomFieldNumberSubType,
  CustomFieldCalculatedSubType,
} from '@/api';

interface Props {
  customField: CustomField & { required?: boolean };
  value?: number;
  hideLabel?: boolean;
  placeholder?: string;
  customError?: string;
  readonly?: boolean;
}
interface Emits {
  (e: 'value-updated', value: number | null): void;
  (e: 'enter'): void;
  (e: 'escape'): void;
}

const props = withDefaults(defineProps<Props>(), {});
const emit = defineEmits<Emits>();

const isCustomFieldValid = computed(() => {
  return (
    props.customField.type === CustomFieldType.NUMBER ||
    (props.customField.type === CustomFieldType.CALCULATED &&
      props.customField.subType === CustomFieldCalculatedSubType.NUMBER)
  );
});

const localVal = ref<number | undefined>(props.value || undefined);
watch(
  () => props.value,
  () => {
    localVal.value = props.value || undefined;
  }
);

const isWholeNumberField = computed(() => {
  return props.customField.subType === CustomFieldNumberSubType.INTEGER;
});

const isPercentageField = computed(() => {
  return props.customField.subType === CustomFieldNumberSubType.PERCENTAGE;
});

const step = computed(() => {
  if (!isNil(props.customField.numberRange?.step)) {
    return props.customField.numberRange?.step;
  }
  if (isWholeNumberField.value || isPercentageField.value) {
    return 1;
  }
  return 0.01;
});

const min = computed(() => {
  if (isPercentageField.value) {
    return 0;
  }
  return props.customField.numberRange?.min || undefined;
});

const max = computed(() => {
  if (isPercentageField.value) {
    return 100;
  }
  return props.customField.numberRange?.max || undefined;
});

const errorMessage = computed(() => {
  if (props.customError) {
    return props.customError;
  }
  if (!localVal.value && localVal.value !== 0 && props.customField.required) {
    return 'This field is required';
  }
  const { numberRange } = props.customField;
  if (numberRange) {
    if (localVal.value && localVal.value < numberRange.min) {
      return `Value must be greater than or equal to ${numberRange.min}`;
    }
    if (localVal.value && localVal.value > numberRange.max) {
      return `Value must be less than or equal to ${numberRange.max}`;
    }
  }
  if (
    isWholeNumberField.value &&
    localVal.value &&
    !Number.isInteger(localVal.value)
  ) {
    return 'Value must be a whole number';
  }

  if (
    isPercentageField.value &&
    localVal.value &&
    (localVal.value < 0 || localVal.value > 100)
  ) {
    return 'Value must be between 0 and 100';
  }
  return '';
});

const showErrorMessage = ref(false);
watch(
  () => props.customError,
  () => {
    showErrorMessage.value = !!props.customError;
  }
);

watch(localVal, () => {
  showErrorMessage.value = false;
});

const placeholderText = computed(() => {
  if (props.placeholder) {
    return props.placeholder;
  }
  if (isPercentageField.value) {
    return '0';
  }
  if (props.readonly) {
    return '';
  }
  return `Enter a ${props.customField.name}`;
});

function onBlur() {
  if (props.customField.required && !localVal.value && localVal.value !== 0) {
    localVal.value = props.value;
    return;
  }
  onValueChange();
}

async function onValueChange() {
  await nextTick();
  if (errorMessage.value) {
    showErrorMessage.value = true;
    return;
  }
  if (localVal.value === props.value) return;
  emit('value-updated', localVal.value ?? null);
}

function onEnter() {
  // Emit current value if changed before emitting enter
  if (localVal.value !== props.value) {
    emit('value-updated', localVal.value ?? null);
  }
  emit('enter');
}

function onEscape() {
  // Reset value if needed
  localVal.value = props.value || undefined;
  emit('escape');
}

async function checkForWholeNumber(event: KeyboardEvent) {
  if (!(isWholeNumberField.value || isPercentageField.value)) return;

  // Allow: backspace, delete, tab, escape, enter, and the decimal point if not a whole number field
  const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter'];
  if (allowedKeys.includes(event.key)) return;

  // Allow: Ctrl/cmd+A, Ctrl/cmd+C, Ctrl/cmd+V, Ctrl/cmd+X
  if (
    (event.ctrlKey || event.metaKey) &&
    ['a', 'c', 'v', 'x'].includes(event.key.toLowerCase())
  )
    return;

  // Allow: home, end, left, right
  if (
    event.key.includes('Arrow') ||
    event.key === 'Home' ||
    event.key === 'End'
  )
    return;

  // Prevent: any other key that is not a number
  if (!/[0-9]/.test(event.key)) {
    event.preventDefault();
  }

  // If it's a whole number or percentage field, prevent the decimal separator
  if (
    (isWholeNumberField.value || isPercentageField.value) &&
    event.key === '.'
  ) {
    event.preventDefault();
  }
}

const isValid = computed(() => {
  return !errorMessage.value;
});

function validate() {
  showErrorMessage.value = !isValid.value;
}

function removeError() {
  showErrorMessage.value = false;
}

defineExpose({
  validate,
  removeError,
  isValid,
});
</script>
